import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import Table from "../../../core/common/dataTable/index"
import TooltipOption from "../../../core/common/tooltipOption"
import TransportModal from "./transportModal"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import { all_routes } from "../../router/all_routes"
import CommonSelect, { Option } from "../../../core/common/commonSelect"
import {
  SORT_OPTIONS,
  statusOptions,
} from "../../../core/common/selectoption/selectoption"
import {
  deleteAllocation,
  getAllAllocations,
  PaginationData,
  Student,
  TransportAllocation,
  updateAllocation,
} from "../../../service/transportAssignVehicles"
import { getAllContractors } from "../../../service/contractor"
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice"
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice"
import TransportCollectFeesModal from "../feescollection/transportCollectFeesModal"
import { getTransportRemainingMonths } from "../../../service/discount"
import {
  printTable,
  exportTableToPdf,
  exportTableToExcel,
} from "../../../utils/exportTable"
import { toast } from "react-toastify"

const getVanRegistration = (van: any): string => {
  return (
    van?.registration_number ||
    van?.registrationNo ||
    van?.registration_no ||
    van?.registration ||
    "-"
  )
}

const getVanType = (van: any): string => {
  const t = van?.contractorId?.contractorType || van?.van_type || van?.vanType || van?.type
  if (!t) return "-"
  return t.charAt(0).toUpperCase() + t.slice(1)
}

const EXPORT_HEADERS = [
  "GR Number",
  "Student Name",
  "Pickup Point",
  "Registration No",
  "Van Type",
  "Driver",
  "Fee Amount",
  "Status",
]

const toExportRows = (rows: TransportAllocation[]): string[][] =>
  rows.map(a => {
    const first = a.student?.first_name || (a.student as any)?.firstName || ""
    const last = a.student?.last_name || (a.student as any)?.lastName || ""
    const driver = a.driver
      ? `${a.driver.first_name} ${a.driver.last_name}`.trim()
      : "Not Assigned"
    return [
      a.student?.grNumber || "-",
      [first, last].filter(Boolean).join(" ") || "-",
      a.pickupStop || "-",
      getVanRegistration(a.van),
      getVanType(a.van),
      driver,
      a.feeAmount != null ? String(a.feeAmount) : "-",
      a.status || "-",
    ]
  })

const TransportAssignVehicle = () => {
  const routes = all_routes
  const navigate = useNavigate()
  const systems = useSelector(selectSystemsForCurrentBranch)
  const currentBranch = useSelector(selectCurrentBranch)
  const currentSession = useSelector(selectCurrentSession)

  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)

  const [data, setData] = useState<TransportAllocation[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [printLoading, setPrintLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0])
  const [filters, setFilters] = useState<{ status?: Option }>({})
  const [appliedFilters, setAppliedFilters] = useState<{
    status?: string
    system?: string
  }>({ system: systems[0]?._id })
  const [selectedSystem, setSelectedSystem] = useState<any>(systems[0] ?? null)
  const [allocationToDelete, setAllocationToDelete] = useState<string | null>(
    null,
  )
  const [allocationToEdit, setAllocationToEdit] =
    useState<TransportAllocation | null>(null)
  const [paginationData, setPaginationData] = useState<PaginationData>()
  const [transportFeeContext, setTransportFeeContext] = useState<{
    studentId: string
    sessionId: string
    studentDisplay: { firstName: string; lastName: string; grNumber: string }
    branchId?: string
  } | null>(null)
  const [showTransportFeeModal, setShowTransportFeeModal] = useState(false)
  const [contractorMap, setContractorMap] = useState<Record<string, string>>(
    {},
  )

  const handleGrNumberClick = useCallback(
    async (record: TransportAllocation) => {
      const studentId = record.student?._id
      if (!studentId) return
      const sessionId =
        (currentSession as any)?._id ?? (currentSession as any)?.id
      if (!sessionId) {
        toast.error("Session not selected")
        return
      }
      try {
        const res = await getTransportRemainingMonths(studentId, sessionId)
        const enrollmentId = res?.data?.currentEnrollment?._id
        if (enrollmentId) {
          navigate(routes.studentDetail.replace(":id", enrollmentId))
        } else {
          toast.error("Current enrollment not found for this student")
        }
      } catch {
        toast.error("Failed to load student details")
      }
    },
    [navigate, routes.studentDetail, currentSession],
  )

  const fetchAssignAllocation = useCallback(
    async (page: number = 1, limit: number = 10) => {
      setLoading(true)
      try {
        const response = await getAllAllocations({
          page,
          limit,
          searchText,
          status: appliedFilters.status || "",
          sortBy: sortBy.value.sortBy,
          sortOrder: sortBy.value.sortOrder as "asc" | "desc" | undefined,
          system: appliedFilters.system,
        } as any)

        const mapped: TransportAllocation[] = (response.data || []).map(
          (item: any) => {
            const student = item.student || {}
            const studentUser = student?.user || student?.userId || {}
            const studentAdmission = student?.admission || {}

            const firstName =
              studentUser?.firstName ??
              studentUser?.first_name ??
              studentAdmission?.firstName ??
              studentAdmission?.first_name ??
              student?.firstName ??
              student?.first_name ??
              ""

            const lastName =
              studentUser?.lastName ??
              studentUser?.last_name ??
              studentAdmission?.lastName ??
              studentAdmission?.last_name ??
              student?.lastName ??
              student?.last_name ??
              ""

            const grNumber = student?.grNumber ?? student?.gr_number ?? ""
            return {
              _id: item._id,
              student: {
                _id: student._id,
                first_name: firstName,
                last_name: lastName,
                grNumber,
              } as Student,
              van: item.van,
              driver: item.driver,
              driverImg:
                item.driver?.img || "assets/img/students/student-01.jpg",
              pickupStop: item.pickupStop,
              startDate: item.startDate,
              status: item.status,
              branch: item.branch,
              feeAmount:
                typeof item.feeAmount === "object" &&
                item.feeAmount != null &&
                "amount" in item.feeAmount
                  ? item.feeAmount.amount
                  : (item.feeAmount ??
                    item.fee_amount?.amount ??
                    item.fee_amount),
            } as TransportAllocation
          },
        )

        setData(mapped)
        if (response.pagination) {
          setPaginationData(response.pagination)
          setTotalCount(response.pagination.total ?? mapped.length)
        }
      } catch (err) {
        console.error("Fetch error:", err)
        toast.error("Failed to fetch allocations")
      } finally {
        setLoading(false)
      }
    },
    [searchText, appliedFilters.status, appliedFilters.system, sortBy.value],
  )

  const loadContractorsMap = useCallback(async () => {
    try {
      const res = await getAllContractors({
        page: 1,
        limit: 500,
        status: "active",
      })
      const list = (res?.data || []) as any[]
      const map: Record<string, string> = {}
      list.forEach(c => {
        const id = c?._id || c?.id
        if (!id) return
        map[id] = c?.name || "-"
      })
      setContractorMap(map)
    } catch {
      setContractorMap({})
    }
  }, [])

  useEffect(() => {
    loadContractorsMap()
  }, [loadContractorsMap])

  const refreshAllocations = useCallback(() => {
    fetchAssignAllocation(
      paginationData?.currentPage || 1,
      paginationData?.limit || 10,
    )
  }, [
    fetchAssignAllocation,
    paginationData?.currentPage,
    paginationData?.limit,
  ])

  useEffect(() => {
    fetchAssignAllocation(1, paginationData?.limit || 10)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAssignAllocation])

  const handleApplyFilters = () => {
    setAppliedFilters(prev => ({
      ...prev,
      status: filters.status?.value ?? "",
    }))

    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
  }

  const handleResetFilters = () => {
    setFilters({})
    setAppliedFilters(prev => ({ ...prev, status: "" }))
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
  }

  const handleSortSelect = (item: (typeof SORT_OPTIONS)[0]) => {
    setSortBy(item)
  }

  const handleDeleteAllocation = async (
    allocationId: string,
  ): Promise<boolean> => {
    try {
      await deleteAllocation(allocationId)
      toast.success("Allocation deleted successfully")
      refreshAllocations()
      return true
    } catch (error) {
      toast.error("Failed to delete allocation")
      return false
    }
  }

  const handleEditAllocation = async (
    allocationId: string,
    updatedData: any,
  ): Promise<boolean> => {
    try {
      const payload = {
        studentId: updatedData.studentId,
        vanId: updatedData.vanId,
        driverId: updatedData.driverId,
        shiftId: updatedData.shiftId,
        route: updatedData.route,
        pickupStop: updatedData.pickupStop,
        feeAmount: updatedData.feeAmount,
        status: updatedData.status,
        sessionId: updatedData.sessionId,
      }

      await updateAllocation(allocationId, payload)
      toast.success("Allocation updated successfully")

      refreshAllocations()
      return true
    } catch (error) {
      console.error("Update allocation error:", error)
      toast.error("Failed to update allocation")
      return false
    }
  }

  const fetchAllForExport = async (): Promise<TransportAllocation[]> => {
    const response = await getAllAllocations({
      page: 1,
      limit: totalCount || 500,
      searchText,
      status: appliedFilters.status || "",
      sortBy: sortBy.value.sortBy,
      sortOrder: sortBy.value.sortOrder as "asc" | "desc" | undefined,
      system: appliedFilters.system,
    } as any)
    return (response.data || []).map((item: any) => {
      const student = item.student || {}
      const studentUser = student?.user || student?.userId || {}
      const studentAdmission = student?.admission || {}
      const firstName =
        studentUser?.firstName ?? studentUser?.first_name ??
        studentAdmission?.firstName ?? studentAdmission?.first_name ??
        student?.firstName ?? student?.first_name ?? ""
      const lastName =
        studentUser?.lastName ?? studentUser?.last_name ??
        studentAdmission?.lastName ?? studentAdmission?.last_name ??
        student?.lastName ?? student?.last_name ?? ""
      return {
        _id: item._id,
        student: { _id: student._id, first_name: firstName, last_name: lastName, grNumber: student?.grNumber ?? "" } as Student,
        van: item.van,
        driver: item.driver,
        pickupStop: item.pickupStop,
        startDate: item.startDate,
        status: item.status,
        branch: item.branch,
        feeAmount: typeof item.feeAmount === "object" && item.feeAmount != null && "amount" in item.feeAmount
          ? item.feeAmount.amount
          : (item.feeAmount ?? item.fee_amount?.amount ?? item.fee_amount),
      } as TransportAllocation
    })
  }

  const handlePrint = async () => {
    if (!totalCount) { toast.warning("No data to print"); return }
    setPrintLoading(true)
    try {
      const all = await fetchAllForExport()
      printTable("Assign Vehicle List", EXPORT_HEADERS, toExportRows(all), {
        logoUrl: currentBranch?.blackLogoUrl,
        brandName: campusBrandName(currentBranch),
        branchLabel: campusBranchLabel(currentBranch),
      })
    } catch {
      toast.error("Failed to prepare print data")
    } finally {
      setPrintLoading(false)
    }
  }

  const handleExportPdf = async () => {
    if (!totalCount) { toast.warning("No data to export"); return }
    setPdfLoading(true)
    try {
      const all = await fetchAllForExport()
      exportTableToPdf("Assign Vehicle List", EXPORT_HEADERS, toExportRows(all), "transport_allocations.pdf", "landscape")
    } catch {
      toast.error("Failed to export PDF")
    } finally {
      setPdfLoading(false)
    }
  }

  const handleExportExcel = async () => {
    if (!totalCount) { toast.warning("No data to export"); return }
    setExcelLoading(true)
    try {
      const all = await fetchAllForExport()
      exportTableToExcel(
        all.map(a => ({
          "GR Number": a.student?.grNumber || "-",
          "Student Name": [a.student?.first_name, a.student?.last_name].filter(Boolean).join(" ") || "-",
          "Pickup Point": a.pickupStop || "-",
          "Registration No": getVanRegistration(a.van),
          "Van Type": getVanType(a.van),
          Driver: a.driver ? `${a.driver.first_name} ${a.driver.last_name}`.trim() : "Not Assigned",
          "Fee Amount": a.feeAmount != null ? a.feeAmount : "-",
          Status: a.status || "-",
        })),
        "Transport Allocations",
        "transport_allocations.xlsx",
      )
    } catch {
      toast.error("Failed to export Excel")
    } finally {
      setExcelLoading(false)
    }
  }

  const handleChange = (page: number, pageSize: number) => {
    fetchAssignAllocation(page, pageSize)
  }

  const handleOpenTransportFees = (record: TransportAllocation) => {
    const studentId = record.student?._id
    if (!studentId) return
    const branch = record.branch
    const branchId =
      typeof branch === "string" ? branch : (branch as { _id?: string })?._id
    setTransportFeeContext({
      studentId,
      sessionId: currentSession?._id ?? "",
      studentDisplay: {
        firstName: record.student?.first_name ?? "",
        lastName: record.student?.last_name ?? "",
        grNumber: record.student?.grNumber ?? "",
      },
      branchId,
    })
    setShowTransportFeeModal(true)
  }

  const handleCloseTransportFeesModal = () => {
    setShowTransportFeeModal(false)
    setTransportFeeContext(null)
  }

  const columns = [
    {
      title: "GR Number",
      dataIndex: "grNumber",
      render: (_: string, record: TransportAllocation) => {
        const studentId = record.student?._id
        const gr = record.student?.grNumber ?? "–"
        if (!studentId) return <span>{gr}</span>
        return (
          <span
            role="button"
            tabIndex={0}
            className="text-dark"
            style={{ cursor: "pointer" }}
            onClick={() => handleGrNumberClick(record)}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleGrNumberClick(record)
              }
            }}
          >
            {gr}
          </span>
        )
      },
      sorter: (a: TransportAllocation, b: TransportAllocation) =>
        (a.student?.grNumber || "").localeCompare(b.student?.grNumber || ""),
    },
    {
      title: "Student Name",
      dataIndex: "student",
      render: (_: unknown, record: TransportAllocation) => {
        const first =
          record.student?.first_name ||
          (record.student as any)?.firstName ||
          ""
        const last =
          record.student?.last_name ||
          (record.student as any)?.lastName ||
          ""
        const name = [first, last].filter(Boolean).join(" ")
        return name || "—"
      },
      sorter: (a: TransportAllocation, b: TransportAllocation) => {
        const aName = [
          a.student?.first_name || "",
          a.student?.last_name || "",
        ].join(" ")
        const bName = [
          b.student?.first_name || "",
          b.student?.last_name || "",
        ].join(" ")
        return aName.localeCompare(bName)
      },
    },
    {
      title: "Pickup Point / Drop Point",
      dataIndex: "pickupStop",
      sorter: (a: TransportAllocation, b: TransportAllocation) =>
        a.pickupStop.localeCompare(b.pickupStop),
    },
    {
      title: "Registration No",
      dataIndex: "van",
      render: (van: any) => getVanRegistration(van),
      sorter: (a: TransportAllocation, b: TransportAllocation) =>
        getVanRegistration(a.van).localeCompare(getVanRegistration(b.van)),
    },
    {
      title: "Van Type",
      dataIndex: "van",
      render: (van: any) => getVanType(van),
      sorter: (a: TransportAllocation, b: TransportAllocation) =>
        getVanType(a.van).localeCompare(getVanType(b.van)),
    },
    {
      title: "Contractor",
      dataIndex: "van",
      render: (van: any) => {
        const v = van as any
        const contractorId =
          v?.contractorId || v?.contractor_id || v?.contractor

        // In your API response, contractorId is usually a string.
        if (typeof contractorId === "string") {
          return contractorMap[contractorId] || "—"
        }

        // If backend returns populated object.
        return contractorId?.name || contractorId?.contractorName || "—"
      },
      sorter: (a: TransportAllocation, b: TransportAllocation) => {
        const getContractorName = (x: any) => {
          const v = x as any
          const id = v?.contractorId || v?.contractor_id || v?.contractor
          if (typeof id === "string") return contractorMap[id] || ""
          return id?.name || id?.contractorName || ""
        }
        return getContractorName(a.van).localeCompare(
          getContractorName(b.van),
        )
      },
    },
    {
      title: "Driver",
      dataIndex: "driver",
      render: (driver: any, record: TransportAllocation) =>
        driver ? (
          <div className="d-flex align-items-center">
            <Link to="#" className="avatar avatar-md">
              {/* <ImageWithBasePath
                src={driver.img || "assets/img/students/student-01.jpg"}
                className="img-fluid rounded-circle"
                alt="img"
              /> */}
            </Link>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <Link to="#">
                  {driver.first_name} {driver.last_name}
                </Link>
              </p>
              <span className="fs-12">{driver.contact_number}</span>
            </div>
          </div>
        ) : (
          "-"
        ),
      sorter: (a: TransportAllocation, b: TransportAllocation) =>
        (
          (a.driver?.first_name || "") + (a.driver?.last_name || "")
        ).localeCompare(
          (b.driver?.first_name || "") + (b.driver?.last_name || ""),
        ),
    },
    {
      title: "Fee Amount",
      dataIndex: "feeAmount",
      render: (feeAmount: number | undefined, record: TransportAllocation) => {
        const vanType =
          record.van?.contractorId?.contractorType || (record.van as any)?.van_type || (record.van as any)?.vanType || ""
        const isPrivateVan = String(vanType).toLowerCase() === "private"
        if (isPrivateVan) return <span className="text-muted">—</span>
        return feeAmount != null ? feeAmount : "-"
      },
      sorter: (a: TransportAllocation, b: TransportAllocation) =>
        (a.feeAmount ?? 0) - (b.feeAmount ?? 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) =>
        text.toLowerCase() === "active" ? (
          <span className="badge badge-soft-success d-inline-flex align-items-center">
            <i className="ti ti-circle-filled fs-5 me-1"></i>
            {text}
          </span>
        ) : (
          <span className="badge badge-soft-danger d-inline-flex align-items-center">
            <i className="ti ti-circle-filled fs-5 me-1"></i>
            {text}
          </span>
        ),
    },
    {
      title: "Collect Fee",
      key: "collectFee",
      render: (_: any, record: TransportAllocation) => {
        const studentId = record.student?._id
        return studentId ? (
          <button
            type="button"
            className="btn btn-light"
            onClick={() => handleOpenTransportFees(record)}
          >
            Collect Fee
          </button>
        ) : (
          <span className="text-muted">–</span>
        )
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: TransportAllocation) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#edit_assign_vehicle"
                  onClick={() => setAllocationToEdit(record)}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#delete-modal"
                  onClick={() => setAllocationToDelete(record._id)}
                >
                  <i className="ti ti-trash-x me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Assign Vehicle</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Assign Vehicle
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchAssignAllocation}
                onPrint={handlePrint}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                printLoading={printLoading}
                exportPdfLoading={pdfLoading}
                exportExcelLoading={excelLoading}
              />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_assign_vehicle"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Assign New Vehicle
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}
          {/* Students List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Assign Vehicle List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={selectedSystem}
                    getOptionValue={item => item._id}
                    getOptionLabel={item => item.name}
                    onChange={systemItem => {
                      setSelectedSystem(systemItem)
                      setAppliedFilters(prev => ({
                        ...prev,
                        system: systemItem?._id,
                      }))
                    }}
                  />
                </div>
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <CommonSelect
                            className="select"
                            options={statusOptions}
                            value={filters?.status}
                            onChange={val =>
                              setFilters({ status: val as Option })
                            }
                          />
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <div
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </div>
                        <div
                          className="btn btn-primary"
                          onClick={handleApplyFilters}
                        >
                          Apply
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${item.label === sortBy.label ? "active" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3 printable-content">
              <Table
                dataSource={data}
                columns={columns}
                setSearchText={setSearchText}
                loading={loading}
                pagination={{ ...paginationData, onChange: handleChange }}
              />
              {/* /Student List */}
            </div>
          </div>
          {/* /Students List */}
        </div>
      </div>
      {/* /Page Wrapper */}
      <TransportModal
        onAllocationCreated={refreshAllocations}
        onDeleteAllocation={handleDeleteAllocation}
        onEditAllocation={handleEditAllocation}
        allocationToDelete={allocationToDelete}
        setAllocationToDelete={setAllocationToDelete}
        allocationToEdit={allocationToEdit}
        setAllocationToEdit={setAllocationToEdit}
        selectedSystem={selectedSystem}
      />
      {showTransportFeeModal && transportFeeContext && (
        <TransportCollectFeesModal
          studentId={transportFeeContext.studentId}
          sessionId={transportFeeContext.sessionId}
          studentDisplay={transportFeeContext.studentDisplay}
          branchId={transportFeeContext.branchId}
          onClose={handleCloseTransportFeesModal}
          openImmediately={true}
        />
      )}
    </>
  )
}

export default TransportAssignVehicle
